import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from '@/integrations/supabase/client';
import {
  Eye, EyeOff, Mail, Lock, Loader2, ShoppingBag,
  Building, User, Phone, MapPin, ChevronRight, ChevronLeft, Check
} from 'lucide-react';

const STEPS = [
  { label: 'Firma Bilgileri', icon: Building },
  { label: 'Hesap Bilgileri', icon: User },
  { label: 'Tamamlandı', icon: Check },
];

const Signup = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Step 1: Company info
  const [companyName, setCompanyName] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [companyPhone, setCompanyPhone] = useState('');

  // Step 2: Account info
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const { toast } = useToast();
  const navigate = useNavigate();
  const { signIn } = useAuth();

  const validateStep1 = () => {
    if (!companyName.trim()) {
      toast({ title: 'Hata', description: 'Firma adı zorunludur', variant: 'destructive' });
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!fullName.trim()) {
      toast({ title: 'Hata', description: 'Ad Soyad zorunludur', variant: 'destructive' });
      return false;
    }
    if (!email.trim()) {
      toast({ title: 'Hata', description: 'E-posta zorunludur', variant: 'destructive' });
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({ title: 'Hata', description: 'Geçersiz e-posta adresi', variant: 'destructive' });
      return false;
    }
    if (password.length < 6) {
      toast({ title: 'Hata', description: 'Şifre en az 6 karakter olmalıdır', variant: 'destructive' });
      return false;
    }
    if (password !== confirmPassword) {
      toast({ title: 'Hata', description: 'Şifreler eşleşmiyor', variant: 'destructive' });
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep === 0 && validateStep1()) {
      setCurrentStep(1);
    }
  };

  const handleBack = () => {
    if (currentStep === 1) {
      setCurrentStep(0);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep2()) return;

    setIsLoading(true);
    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/account-signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': SUPABASE_PUBLISHABLE_KEY,
        },
        body: JSON.stringify({
          email: email.trim(),
          password,
          full_name: fullName.trim(),
          company_name: companyName.trim(),
          company_address: companyAddress.trim() || undefined,
          company_phone: companyPhone.trim() || undefined,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Hesap oluşturulamadı');
      }

      // Auto sign-in
      const { error: signInError } = await signIn(email.trim(), password);
      if (signInError) {
        toast({
          title: 'Uyarı',
          description: 'Hesabınız oluşturuldu ancak otomatik giriş yapılamadı. Lütfen giriş yapın.',
          variant: 'destructive',
        });
        navigate('/login');
        return;
      }

      // Show success step
      setCurrentStep(2);
    } catch (err: any) {
      toast({
        title: 'Hata',
        description: err.message || 'Bir hata oluştu',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden glass-panel-strong">

        {/* Left Side - Branding */}
        <div className="relative p-8 sm:p-12 lg:p-16 flex flex-col justify-center min-h-[300px] lg:min-h-[600px]">
          <div className="absolute inset-0 bg-gradient-to-br from-accent/20 via-accent/10 to-transparent"></div>

          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-2xl bg-accent/20 flex items-center justify-center">
                <ShoppingBag className="w-6 h-6 text-accent" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">MüşteriX</h1>
                <p className="text-sm text-muted-foreground">Mağaza Yönetimi Sistemi</p>
              </div>
            </div>

            <div className="space-y-6">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
                Hemen
                <span className="block text-accent">Başlayın</span>
              </h2>

              <p className="text-lg text-foreground/80 leading-relaxed">
                Ücretsiz hesabınızı oluşturun ve mağaza yönetiminizi modernleştirmeye başlayın.
              </p>

              <div className="space-y-3 pt-4">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-accent"></div>
                  <span className="text-foreground/70">Ücretsiz başlangıç</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-accent"></div>
                  <span className="text-foreground/70">Kredi kartı gerekmez</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-accent"></div>
                  <span className="text-foreground/70">Anında kullanıma hazır</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Signup Wizard */}
        <div className="p-8 sm:p-12 lg:p-16 flex flex-col justify-center bg-white/5">
          <div className="w-full max-w-sm mx-auto">

            {/* Step Indicator */}
            <div className="flex items-center justify-center gap-2 mb-8">
              {STEPS.map((step, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                      index < currentStep
                        ? 'bg-accent text-accent-foreground'
                        : index === currentStep
                        ? 'bg-accent text-accent-foreground'
                        : 'bg-white/10 text-muted-foreground'
                    }`}
                  >
                    {index < currentStep ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      index + 1
                    )}
                  </div>
                  {index < STEPS.length - 1 && (
                    <div
                      className={`w-8 h-0.5 ${
                        index < currentStep ? 'bg-accent' : 'bg-white/10'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Step 1: Company Info */}
            {currentStep === 0 && (
              <div>
                <div className="mb-8">
                  <h3 className="text-2xl sm:text-3xl font-bold mb-2">Firma Bilgileri</h3>
                  <p className="text-foreground/70">Firmanız hakkında bilgi verin</p>
                </div>

                <div className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="companyName" className="text-sm font-medium">
                      Firma Adı <span className="text-destructive">*</span>
                    </Label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="companyName"
                        placeholder="Firma adınızı girin"
                        value={companyName}
                        onChange={(e) => setCompanyName(e.target.value)}
                        className="pl-10 glass-panel"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="companyAddress" className="text-sm font-medium">
                      Adres
                    </Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="companyAddress"
                        placeholder="Firma adresi (isteğe bağlı)"
                        value={companyAddress}
                        onChange={(e) => setCompanyAddress(e.target.value)}
                        className="pl-10 glass-panel"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="companyPhone" className="text-sm font-medium">
                      Telefon
                    </Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="companyPhone"
                        placeholder="Telefon numarası (isteğe bağlı)"
                        value={companyPhone}
                        onChange={(e) => setCompanyPhone(e.target.value)}
                        className="pl-10 glass-panel"
                      />
                    </div>
                  </div>

                  <Button onClick={handleNext} className="w-full gap-2">
                    Devam Et
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Account Info */}
            {currentStep === 1 && (
              <div>
                <div className="mb-8">
                  <h3 className="text-2xl sm:text-3xl font-bold mb-2">Hesap Bilgileri</h3>
                  <p className="text-foreground/70">Giriş bilgilerinizi oluşturun</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="fullName" className="text-sm font-medium">
                      Ad Soyad <span className="text-destructive">*</span>
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="fullName"
                        placeholder="Ad ve soyadınız"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="pl-10 glass-panel"
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signupEmail" className="text-sm font-medium">
                      E-posta <span className="text-destructive">*</span>
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="signupEmail"
                        type="email"
                        placeholder="ornek@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 glass-panel"
                        disabled={isLoading}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="signupPassword" className="text-sm font-medium">
                      Şifre <span className="text-destructive">*</span>
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="signupPassword"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="En az 6 karakter"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="pl-10 pr-10 glass-panel"
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-sm font-medium">
                      Şifre Tekrar <span className="text-destructive">*</span>
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Şifrenizi tekrar girin"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="pl-10 pr-10 glass-panel"
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button type="button" variant="outline" onClick={handleBack} className="gap-2" disabled={isLoading}>
                      <ChevronLeft className="w-4 h-4" />
                      Geri
                    </Button>
                    <Button type="submit" className="flex-1 gap-2" disabled={isLoading}>
                      {isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Hesap oluşturuluyor...
                        </>
                      ) : (
                        <>
                          Hesap Oluştur
                          <ChevronRight className="w-4 h-4" />
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* Step 3: Complete */}
            {currentStep === 2 && (
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-6">
                  <Check className="w-10 h-10 text-accent" />
                </div>

                <h3 className="text-2xl sm:text-3xl font-bold mb-3">Hesabınız Hazır!</h3>
                <p className="text-foreground/70 mb-8">
                  <span className="font-medium text-foreground">{companyName}</span> firması başarıyla oluşturuldu.
                  Ücretsiz hesabınızla hemen kullanmaya başlayabilirsiniz.
                </p>

                <Button onClick={() => navigate('/')} className="w-full gap-2" size="lg">
                  Panele Git
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}

            {/* Login link */}
            {currentStep < 2 && (
              <div className="mt-6 pt-6 border-t border-white/10 text-center">
                <p className="text-sm text-foreground/60">
                  Zaten hesabınız var mı?{' '}
                  <button
                    onClick={() => navigate('/login')}
                    className="text-accent hover:text-accent/80 transition-colors font-medium"
                  >
                    Giriş Yapın
                  </button>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
